import React, { useState } from 'react';
import { signIn, type SignInInput, confirmSignIn } from 'aws-amplify/auth';
import { useNavigate } from "react-router-dom";
import { useLocation } from 'react-router-dom';

const LoginForm: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [mfaCode, setMfaCode] = useState('');
    const [showMfaInput, setShowMfaInput] = useState(false);
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [signinError, setSigninError] = useState('');
    const location = useLocation();

    async function handleSignIn({ username, password }: SignInInput) {
        try {
            const { nextStep } = await signIn({ username, password });
            if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
                setShowMfaInput(true);
            } else {
                // Redirect to authenticated page
            }
        } catch (error) {
            if (error instanceof Error) {
                switch ((error as any).name) {
                    case "NotAuthorizedException":
                        setSigninError(error.message);
                        break;
                    case "UserAlreadyAuthenticatedException":
                        navigate("/dashboard");
                        break;
                }
            }
            console.error('Error signing in:', error);
        }
    }

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        clearMessages();
        setIsSubmitting(true);
        try {
            const username = email;
            await handleSignIn({ username, password });
        } catch (error) {
            console.error('Error signing in:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    async function handleSignInConfirmation(otpCode: string) {
        try {
            await confirmSignIn({ challengeResponse: otpCode });
            // Redirect to authenticated page
            navigate("/dashboard");
        } catch (error) {
            if (error instanceof Error) {
                switch ((error as any).name) {
                    case "CodeMismatchException":
                        setSigninError("Incorrect verification code.");
                        break;
                    case "ExpiredCodeException":
                        setSigninError("Verification code expired.");
                        break;
                    case "TooManyFailedAttemptsException":
                        setSigninError("Too many failed attempts.");
                        break;
                }
            } else {
                console.log(error);
            }
        }
    }

    const handleMfaSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        console.log("attempting signin");
        try {
            await handleSignInConfirmation(mfaCode);
        } catch (error) {
            console.error('Error confirming MFA:', error);
        }
    };

    const clearMessages = () => {
        if (location.state) {
            location.state.registrationSuccess = false;
            location.state.resetSuccess = false;
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 flex justify-center items-center">
            <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
                <div className="flex justify-center">
                    <img
                        src="/images/jack-logo.jpg"
                        alt="Jack Logo"
                        style={{ maxWidth: "80%", maxHeight: "30%" }}
                        className='pb-8'
                    />
                </div>
                {location.state?.registrationSuccess && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                        Registration successful! You can now log in.
                    </div>
                )}
                {location.state?.resetSuccess && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                        Password reset successful! You can now log in.
                    </div>
                )}
                <h2 className="text-2xl font-bold mb-6 text-center">Login</h2>
                {signinError && (
                    <p className="mt-2 text-sm text-red-500">{signinError}</p>)}
                {!showMfaInput && (
                    <div>
                        <form onSubmit={handleLogin}>
                            <div className="mb-4">
                                <label htmlFor="email" className="block mb-2 font-bold text-gray-700">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    placeholder="name@host.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>
                            <div className="mb-6">
                                <label htmlFor="password" className="block mb-2 font-bold text-gray-700">
                                    Password
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                                <div>
                                    <a href="/forgot-password" className="text-blue-600 hover:underline">
                                        Forgot your password?
                                    </a>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full bg-[#43a4db] text-white font-bold py-2 px-4 rounded-md hover:bg-[#2a6e95] transition duration-300 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                            >
                                {isSubmitting ? 'Logging in...' : 'Login'}
                            </button>
                        </form>
                        <div className="mt-6 text-center">
                            <span className="text-gray-600">Don't have an account? </span>
                            <a href="/register" className="text-blue-600 hover:underline">
                                Sign up
                            </a>
                        </div>
                    </div>
                )}
                {showMfaInput && (
                    <form onSubmit={handleMfaSubmit} className="mt-6">
                        <div className="mb-4">
                            <label htmlFor="mfaCode" className="block mb-2 font-bold text-gray-700">
                                Verification Code
                            </label>
                            <input
                                type="text"
                                id="mfaCode"
                                autoComplete='one-time-code'
                                placeholder="Enter Verification Code"
                                value={mfaCode}
                                onChange={(e) => setMfaCode(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            />
                        </div>
                        <button
                            type="submit"
                            className="w-full bg-[#43a4db] text-white font-bold py-2 px-4 rounded-md hover:bg-[#2a6e95] transition duration-300"
                        >
                            Verify
                        </button>
                    </form>
                )}

            </div>
        </div>
    );
};

export default LoginForm;